import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { IonCol, ToastController } from '@ionic/angular';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { captureException } from '@sentry/browser';
import { AuthState } from '@wilson/auth/core';
import {
  CorePayrollCategory,
  OvertimesToSum,
  OvertimeTimeRangeKeys,
  PayrollUnitName,
} from '@wilson/interfaces';
import { getOvertimeDateRange } from '@wilson/utils';
import { endOfMonth, startOfMonth } from 'date-fns';
import {
  catchError,
  map,
  merge,
  Observable,
  of,
  share,
  skip,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import { WorkTimeManagementDataService } from '../../../work-time-management-data/work-time-management-data.service';
import { WorkingTimeStatsService } from '../working-time-cards/working-time-stats.service';
import { WorkingTimeDashboardContextService } from '../working-time-dashboard/working-time-dashboard-context.service';
import { SectionWrapperComponent } from '../section-wrapper/section-wrapper.component';
import { WorktimeMetricsCardComponent } from '../worktime-metrics-card/worktime-metrics-card.component';
import { LocalizedMonthYearFormatPipe } from '@wilson/localization';

type OvertimeStats = {
  overtimeByYear: number;
  overtimeCompensationByYear: number;
  restOfOvertimesByYear: (CorePayrollCategory & {
    value: number;
  })[];
};

const INITIAL_OVERTIME_STATS: OvertimeStats = {
  overtimeByYear: 0,
  overtimeCompensationByYear: 0,
  restOfOvertimesByYear: [],
};

@Component({
  selector: 'wilson-working-time-overtimes',
  templateUrl: './working-time-overtimes.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    WorktimeMetricsCardComponent,
    SectionWrapperComponent,
    IonCol,
  ],
})
export class WorkingTimeOvertimesComponent {
  private readonly dashboardContext = inject(
    WorkingTimeDashboardContextService,
  );
  private readonly workTimeDataService = inject(WorkTimeManagementDataService);
  private readonly workingTimeStatsService = inject(WorkingTimeStatsService);
  private readonly store = inject(Store);
  private readonly toastController = inject(ToastController);
  private readonly translateService = inject(TranslateService);
  private readonly localizedMonthYearFormat = inject(
    LocalizedMonthYearFormatPipe,
  );

  protected readonly PayrollUnitName = PayrollUnitName;
  protected readonly loadingSignal = signal(true);
  protected readonly loadingWorktimeStats =
    this.workingTimeStatsService.loadingWorktimeStats;

  protected readonly overtimeByCurrentMonth = computed(() => {
    const stats = this.workingTimeStatsService.worktimeStats();
    let time = 0;
    if (stats.length >= 2) {
      const [minutesWork, minutesPlanned] = stats;

      time = minutesWork - minutesPlanned;
    }

    return time;
  });

  private readonly overtimeDateRange = computed(() =>
    getOvertimeDateRange(new Date(this.dashboardContext.monthYear())),
  );

  private readonly fetchTrigger$ = merge(
    this.dashboardContext.monthYear$.pipe(
      map((monthYear) => ({ monthYear, refresh: false as const })),
    ),
    this.dashboardContext.refreshTrigger$.pipe(
      skip(1),
      withLatestFrom(this.dashboardContext.monthYear$),
      map(([, monthYear]) => ({ monthYear, refresh: true as const })),
    ),
  ).pipe(share());

  protected readonly overtimeStats = toSignal(
    this.fetchTrigger$.pipe(
      tap(() => this.loadingSignal.set(true)),
      switchMap(({ monthYear }) => this.getOvertimeStatValues(monthYear)),
    ),
    { initialValue: INITIAL_OVERTIME_STATS },
  );

  private getOvertimeStatValues(
    selectedMonthYear: string,
  ): Observable<OvertimeStats> {
    const userId = this.store.selectSnapshot(AuthState.userId) as string;
    const selectedDate = new Date(selectedMonthYear);
    const selectedMonthStart = startOfMonth(selectedDate);
    const { startDate, endDate } = this.overtimeDateRange();

    return this.workTimeDataService
      .getOvertimeSummaryByRanges(userId, {
        [OvertimeTimeRangeKeys.SelectedYear]: {
          timeRange: { start: startDate, end: endDate },
          fields: [
            OvertimesToSum.OvertimeInMinutes,
            OvertimesToSum.RestOfOvertimes,
          ],
        },
        [OvertimeTimeRangeKeys.SelectedMonth]: {
          timeRange: {
            start: selectedMonthStart,
            end: endOfMonth(selectedMonthStart),
          },
          fields: [
            OvertimesToSum.OvertimeInMinutes,
            OvertimesToSum.OvertimeCompensationInMinutes,
          ],
        },
      })
      .pipe(
        map((overtimeData) => ({
          overtimeByYear: overtimeData.selectedYear?.overtimeInMinutes ?? 0,
          overtimeCompensationByYear:
            overtimeData.selectedMonth?.overtimeCompensationInMinutes ?? 0,
          restOfOvertimesByYear:
            overtimeData.selectedYear?.restOfOvertimes ?? [],
        })),
        tap(() => this.loadingSignal.set(false)),
        catchError((e) => {
          this.handleError(e);
          this.loadingSignal.set(false);
          return of(INITIAL_OVERTIME_STATS);
        }),
      );
  }

  protected readonly overtimeMonthYearLabel = computed(() =>
    this.localizedMonthYearFormat.transform(this.overtimeDateRange().endDate),
  );

  private async handleError(e: Error): Promise<void> {
    captureException(e);
    const toast = await this.toastController.create({
      color: 'danger',
      message: this.translateService.instant('general.errorMessage'),
      duration: 3000,
      position: 'top',
    });
    await toast.present();
  }
}
